import { compact, groupBy } from 'lodash';
import { useMemo } from 'react';

import { ButtonWithPopoverProps } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { CommonAiSuggestionFragment } from '@/modules/aiSuggestions/graphql/aiSuggestions.generated';
import {
  EditTestamentaryEntitySection,
  getTestamentaryEntitySectionOptions,
} from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreen.types';
import { getAISuggestionsForTestamentaryEntitySection } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreen.utils';
import { EstatePage_UnacknowledgedAiSuggestionFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AiSuggestionKind } from '@/types/schema';

import { isSuggestionForPrimaryDeathOrder } from './estateOnboarding.utils';
import { EstateOnboardingSuggestionsButton } from './EstateOnboardingSuggestionsButton';

export interface EstateOnboardingTestamentaryEntitySuggestionsButtonProps
  extends Partial<ButtonWithPopoverProps> {
  testamentaryEntityId: string;
  isGenerating?: boolean;
  entitySuggestionsV2?: EstatePage_UnacknowledgedAiSuggestionFragment[];
  /**
   * Callback when a section suggestion menu item is clicked.
   */
  onEditTestamentaryEntitySectionSuggestionClick?: (
    section: EditTestamentaryEntitySection
  ) => void;
}

/**
 * Button that displays AI suggestions grouped by section for a testamentary entity.
 * Similar to EstateOnboardingEntitySuggestionsButton but uses testamentary entity sections.
 */
export function EstateOnboardingTestamentaryEntitySuggestionsButton({
  testamentaryEntityId,
  isGenerating = false,
  entitySuggestionsV2,
  onEditTestamentaryEntitySectionSuggestionClick,
  size = 'xs',
  variant = 'secondary',
  popperVariant = 'menuBelow',
  sx,
  ...props
}: EstateOnboardingTestamentaryEntitySuggestionsButtonProps) {
  const { defaultFirstToDieClientId, primaryClients } =
    useHouseholdDetailsContext();

  const isTwoClientHousehold = (primaryClients?.length ?? 0) >= 2;

  // Memoize testamentary entity section suggestions logic
  const sectionSuggestions = useMemo(() => {
    // Get the sections for testamentary entities
    const sectionOptions =
      getTestamentaryEntitySectionOptions(isTwoClientHousehold);

    // Group suggestions by kind
    const suggestions = entitySuggestionsV2 || [];
    const suggestionsByKind = groupBy(suggestions, 'kind') as Record<
      AiSuggestionKind,
      CommonAiSuggestionFragment[]
    >;

    // For each section, get the suggestions and compute the count
    const sectionsData = compact(
      sectionOptions.map((option) => {
        // Skip DISPOSITIONS section - handled separately via dpSuggestions
        if (option.section === EditTestamentaryEntitySection.DISPOSITIONS) {
          return null;
        }

        const sectionSuggestions = getAISuggestionsForTestamentaryEntitySection(
          option.section,
          suggestionsByKind
        ).filter((s) => !s.acceptanceStatus);

        if (sectionSuggestions.length === 0) {
          return null;
        }

        return {
          section: option.section,
          count: sectionSuggestions.length,
          label: option.label,
        };
      })
    );

    return sectionsData;
  }, [entitySuggestionsV2, isTwoClientHousehold]);

  // Calculate DP suggestions count
  const dpSuggestionsCount = useMemo(() => {
    // Scope DP suggestions to the primary death order so the count reflects
    // only the visible death-order scenario. Per-death-order counts are
    // surfaced separately within the dispositions form.
    return (entitySuggestionsV2 || []).filter(
      (suggestion) =>
        suggestion.kind === AiSuggestionKind.DpRecipientV2 &&
        !suggestion.acceptanceStatus &&
        isSuggestionForPrimaryDeathOrder(
          suggestion,
          defaultFirstToDieClientId ?? undefined
        )
    ).length;
  }, [defaultFirstToDieClientId, entitySuggestionsV2]);

  // Calculate total suggestions count
  const totalEntityDetailsSuggestions = sectionSuggestions.reduce(
    (sum, item) => sum + item.count,
    0
  );
  const totalCount = totalEntityDetailsSuggestions + dpSuggestionsCount;

  const suggestionOptions = [
    ...sectionSuggestions.map(({ section, count, label }) => ({
      key: `${testamentaryEntityId}-${section}`,
      label,
      count,
      onClick: () => onEditTestamentaryEntitySectionSuggestionClick?.(section),
    })),
    ...(dpSuggestionsCount > 0
      ? [
          {
            key: `${testamentaryEntityId}-dispositions`,
            label: 'Dispositions',
            count: dpSuggestionsCount,
            onClick: () =>
              onEditTestamentaryEntitySectionSuggestionClick?.(
                EditTestamentaryEntitySection.DISPOSITIONS
              ),
          },
        ]
      : []),
  ];

  return (
    <EstateOnboardingSuggestionsButton
      dataTestId={`estate-subtrust-suggestions-button-${testamentaryEntityId}`}
      totalCount={totalCount}
      size={size}
      variant={variant}
      popperVariant={popperVariant}
      isGenerating={isGenerating}
      suggestionOptions={suggestionOptions}
      sx={{
        ...sx,
        minWidth: '148px',
      }}
      {...props}
    />
  );
}
